import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../lib/adminAuth';
import { Home, Users, Wrench, TrendingUp, ArrowRight, Loader2, Send } from 'lucide-react';

const StatCard = ({ label, value, icon: Icon, accent = 'text-[#9a1b2f] bg-[#fef2f4]' }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="mt-4 text-3xl font-black text-gray-900">{value}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
  </div>
);

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/stats').then((r) => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <AdminLayout title="Dashboard">
      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#9a1b2f]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard label="Total Properties" value={stats?.properties ?? 0} icon={Home} />
            <StatCard label="Property Interests" value={stats?.interests ?? 0} icon={Users} accent="text-emerald-600 bg-emerald-50" />
            <StatCard label="Service Requests" value={stats?.service_requests ?? 0} icon={Wrench} accent="text-amber-600 bg-amber-50" />
            <StatCard label="Property Submissions" value={stats?.property_submissions ?? 0} icon={Send} accent="text-purple-600 bg-purple-50" />
            <StatCard label="New Requests" value={(stats?.new_service_requests ?? 0) + (stats?.new_property_submissions ?? 0)} icon={TrendingUp} accent="text-blue-600 bg-blue-50" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mt-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900">Properties by Listing Type</h3>
              <div className="mt-4 space-y-3">
                {Object.entries(stats?.by_type || {}).map(([k, v]) => {
                  const total = Object.values(stats?.by_type || {}).reduce((s, x) => s + x, 0) || 1;
                  const pct = Math.round((v / total) * 100);
                  return (
                    <div key={k}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="capitalize text-gray-700">{k.replace('_', ' ')}</span>
                        <span className="font-semibold text-gray-900">{v}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#9a1b2f] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#9a1b2f] to-[#7a1524] text-white rounded-2xl p-6">
              <h3 className="font-bold text-lg">Quick Actions</h3>
              <p className="text-xs opacity-90 mt-1">Manage your listings and respond to leads.</p>
              <div className="mt-5 space-y-2">
                <Link to="/admin/properties/new" className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition text-sm font-semibold">
                  Add New Property <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/admin/interests" className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition text-sm font-semibold">
                  View Property Interests <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/admin/service-requests" className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition text-sm font-semibold">
                  View Service Requests <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboardPage;
